import { isSecretAskBlock } from "./action-approval.js";

type AskSnapshot = {
  messages: readonly {
    id: string;
    runId?: string | null;
    blocks: readonly { kind: string; status?: string; input?: string; approvalEffectId?: string }[];
  }[];
  run?: { id: string; status: string; userId?: string } | null;
  activeRuns?: readonly { id: string; status: string; userId?: string }[];
};

export function answerableAskMessageIds(snapshot: AskSnapshot | null): Set<string> {
  const answerable = new Set<string>();
  if (!snapshot) return answerable;
  const waitingRunIds = new Set(
    (snapshot.activeRuns ?? (snapshot.run ? [snapshot.run] : []))
      .filter((run) => run.status === "waiting_input")
      .map((run) => run.id),
  );
  for (const message of snapshot.messages) {
    if (!message.runId || !waitingRunIds.has(message.runId)) continue;
    if (message.blocks.some((block) => block.kind === "ask" && block.status !== "answered")) {
      answerable.add(message.id);
    }
  }
  return answerable;
}

/** Secret requests can only be dismissed by the user of their run. */
export function dismissableAskMessageIds(
  snapshot: AskSnapshot | null,
  userId?: string,
): Set<string> {
  const dismissable = new Set<string>();
  if (!snapshot || !userId) return dismissable;
  const runs = snapshot.activeRuns ?? (snapshot.run ? [snapshot.run] : []);
  const ownedRunIds = new Set(
    runs
      .filter((run) => run.status === "waiting_input" && run.userId === userId)
      .map((run) => run.id),
  );
  for (const message of snapshot.messages) {
    if (
      message.runId &&
      ownedRunIds.has(message.runId) &&
      message.blocks.some((block) => isSecretAskBlock(block) && block.status !== "answered")
    ) {
      dismissable.add(message.id);
    }
  }
  return dismissable;
}

export function selectedAskActionLabel(
  answer: string,
  actions?: readonly { id: string; label: string }[],
): string {
  return resolveAskChoice(answer, actions)?.label ?? answer;
}

export function resolveAskChoice(
  answer: string,
  actions?: readonly { id: string; label: string }[],
): { id: string; label: string } | undefined {
  if (!actions?.length) return undefined;
  const trimmed = answer.trim();
  if (!trimmed) return undefined;
  const byId = actions.find((action) => action.id === trimmed);
  if (byId) return byId;
  const lower = trimmed.toLowerCase();
  return actions.find((action) => action.label.trim().toLowerCase() === lower);
}

/** At most this many questions share one card. */
export const ASK_QUESTIONS_MAX = 4;
const ASK_QUESTION_MAX_LENGTH = 240;
const ASK_OPTION_MAX_LENGTH = 80;
/** An answer to one question on a card: a choice, or the owner's own words. */
export const ASK_QUESTION_ANSWER_MAX_LENGTH = 500;

export type AskQuestion = { question: string; options: string[] };

/**
 * ask_user's `questions`, checked: one to four, each a short question with two to four unique,
 * short answers. Returns the reason when they are not.
 */
export function parseAskQuestions(raw: unknown): AskQuestion[] | { error: string } {
  const error = {
    error: `questions takes one to ${ASK_QUESTIONS_MAX} items, each a question with two to four unique, non-empty options`,
  };
  if (!Array.isArray(raw) || raw.length < 1 || raw.length > ASK_QUESTIONS_MAX) return error;
  const questions: AskQuestion[] = [];
  for (const entry of raw) {
    const fields = entry && typeof entry === "object" ? (entry as Record<string, unknown>) : {};
    const question = String(fields.question ?? "").trim();
    const options = Array.isArray(fields.options)
      ? fields.options.map((option) => String(option).trim())
      : [];
    if (
      !question ||
      question.length > ASK_QUESTION_MAX_LENGTH ||
      options.length < 2 ||
      options.length > 4 ||
      options.some((option) => !option || option.length > ASK_OPTION_MAX_LENGTH) ||
      new Set(options).size !== options.length
    ) {
      return error;
    }
    questions.push({ question, options });
  }
  return questions;
}

/**
 * A questions card as plain text, for clients that read only `text` (and notifications): the
 * intro, then each question with its lettered options. The owner can answer it in their words.
 */
export function askQuestionsText(intro: string, questions: readonly AskQuestion[]): string {
  return [
    intro.trim(),
    ...questions.map(
      (entry, index) =>
        `${index + 1}. ${entry.question}\n${entry.options
          .map((option, choice) => `${String.fromCharCode(65 + choice)}) ${option}`)
          .join("  ")}`,
    ),
  ]
    .filter(Boolean)
    .join("\n\n");
}

/**
 * What answering a questions card says. A client that lays the card out sends
 * `{"answers": [...]}`, one per question; anything else is the owner's own words for the whole
 * card. Null when the answers do not fit the card.
 */
export function askQuestionsAnswer(
  questions: readonly AskQuestion[],
  answer: string,
): { answers: string[] | null; text: string } | null {
  const trimmed = answer.trim();
  if (!trimmed.startsWith("{")) return trimmed ? { answers: null, text: trimmed } : null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmed);
  } catch {
    return { answers: null, text: trimmed };
  }
  const list = (parsed as { answers?: unknown } | null)?.answers;
  if (!Array.isArray(list) || list.length !== questions.length) return null;
  const answers = list.map((entry) => (typeof entry === "string" ? entry.trim() : ""));
  if (answers.some((entry) => !entry || entry.length > ASK_QUESTION_ANSWER_MAX_LENGTH)) {
    return null;
  }
  return {
    answers,
    text: questions
      .map((entry, index) => `${index + 1}. ${entry.question}\nAnswer: ${answers[index]}`)
      .join("\n"),
  };
}

/** The intro of a questions card's text (see askQuestionsText), without the questions. */
export function askQuestionsIntro(text: string): string {
  return text.startsWith("1. ") ? "" : text.split("\n\n1. ")[0]!.trim();
}
